import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import qs from 'qs';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { ISearchUsersForAdminRequest, PaginationRequest, SearchUsersForAdminRequest } from '@joc/api-gateway';
import { Store } from 'redux/root';
import useThunkDispatch from '../../../../core/customHooks/useThunkDispatch';
// core
import { INITIAL_PAGINATION } from 'core/constants';
import { Pagination, SetPagination } from 'core/types';
// components
import TabNav from 'shared/components/TabNav';
import TabNavItem from 'shared/components/TabNav/TabNavItem';
import {
	getCompaniesUsersAll,
	getCompaniesUsersCoordinators,
	getCompaniesUsersMarketers,
	resetCompaniesUsers,
	getCompaniesUsersAdmins,
	getCompaniesUsersTotals,
} from 'redux/companiesUsers-service/actions';
import { resetError } from 'redux/error-service/action';
import UsersTable from '../UsersTable';
// styles
import styles from './UserListContent.module.scss';

const parseQueryString = (search: string): SearchUsersForAdminRequest => {
	return qs.parse(search, { ignoreQueryPrefix: true }) as unknown as SearchUsersForAdminRequest;
};

enum TABS {
	ALL,
	ADMIN,
	COORDINATOR,
	MARKETER,
}

const UserListContent: FC = () => {
	const location = useLocation();
	const history = useHistory();
	const dispatch = useThunkDispatch();

	const activeTab = useMemo(() => {
		if (location.search.includes('admins')) return TABS.ADMIN;
		if (location.search.includes('coordinators')) return TABS.COORDINATOR;
		if (location.search.includes('marketers')) return TABS.MARKETER;
		return TABS.ALL;
	}, [location.search]);

	const [pagination, setPagination] = useState<Pagination>(INITIAL_PAGINATION);
	const [isLoading, setIsLoading] = useState(true);
	const activeRequestIdRef = useRef<number>(0);

	const handleSetPagination: SetPagination = useCallback(value => {
		setPagination(prev => {
			const resolved = typeof value === 'function' ? value(prev) : value;
			if (prev.skip === resolved.skip && prev.take === resolved.take) {
				return prev;
			}
			return resolved;
		});
	}, []);

	const usersRecordsTotals = useSelector((store: Store) => store.companiesUsersRedux.totals);
	const currentTotal = useSelector((store: Store) => store.companiesUsersRedux.total);

	const isSearchApplied = useMemo(() => {
		const searchParams = parseQueryString(location.search);
		return Boolean(searchParams.fullTextSearch?.value?.trim());
	}, [location.search]);

	const allCounter = activeTab === TABS.ALL && isSearchApplied ? currentTotal : usersRecordsTotals.all;
	const adminsCounter = activeTab === TABS.ADMIN && isSearchApplied ? currentTotal : usersRecordsTotals.admins;
	const coordinatorsCounter =
		activeTab === TABS.COORDINATOR && isSearchApplied ? currentTotal : usersRecordsTotals.coordinators;
	const marketersCounter =
		activeTab === TABS.MARKETER && isSearchApplied ? currentTotal : usersRecordsTotals.marketers;

	const fetchUsers = useCallback(
		async (currentPagination: Pagination) => {
			activeRequestIdRef.current += 1;
			const requestId = activeRequestIdRef.current;
			const shouldCancel = () => activeRequestIdRef.current !== requestId;

			setIsLoading(true);
			try {
				const searchParams = parseQueryString(location.search);
				const requestPayload: ISearchUsersForAdminRequest = {
					...searchParams,
					pagination: PaginationRequest.fromJS(currentPagination),
				};

				if (location.search.includes('admins')) {
					await dispatch(getCompaniesUsersAdmins(requestPayload, shouldCancel));
				} else if (location.search.includes('coordinators')) {
					await dispatch(getCompaniesUsersCoordinators(requestPayload, shouldCancel));
				} else if (location.search.includes('marketers')) {
					await dispatch(getCompaniesUsersMarketers(requestPayload, shouldCancel));
				} else {
					await dispatch(getCompaniesUsersAll(requestPayload, shouldCancel));
				}
			} catch (error: any) {
				if (!shouldCancel()) {
					console.error(error);
				}
			} finally {
				if (!shouldCancel()) {
					setIsLoading(false);
				}
			}
		},
		[dispatch, location.search]
	);

	useEffect(() => {
		dispatch(resetCompaniesUsers());
		setPagination(INITIAL_PAGINATION);
		fetchUsers(INITIAL_PAGINATION);
	}, [location.search, fetchUsers, dispatch]);

	useEffect(() => {
		if (pagination.skip > 0) {
			fetchUsers(pagination);
		}
	}, [pagination, fetchUsers]);

	useEffect(() => {
		dispatch(getCompaniesUsersTotals);

		return () => {
			activeRequestIdRef.current += 1;
			dispatch(resetCompaniesUsers());
			dispatch(resetError());
		};
	}, [dispatch]);

	return (
		<div className={styles.content__wrapper}>
			<div className={styles.content}>
				<TabNav>
					<TabNavItem
						parentClassName={styles.tabNavItem}
						counter={allCounter}
						title="All"
						activeTab={activeTab === TABS.ALL}
						clickHandler={() => {
							history.push({ search: '' });
						}}
					/>
					<TabNavItem
						parentClassName={styles.generateGroup}
						counter={adminsCounter}
						title="Admins"
						activeTab={activeTab === TABS.ADMIN}
						clickHandler={() => {
							history.push({ search: '?admins' });
						}}
					/>
					<TabNavItem
						parentClassName={styles.generateGroup}
						counter={coordinatorsCounter}
						title="Coordinators"
						activeTab={activeTab === TABS.COORDINATOR}
						clickHandler={() => {
							history.push({ search: '?coordinators' });
						}}
					/>
					<TabNavItem
						parentClassName={styles.generateGroup}
						counter={marketersCounter}
						title="Marketers"
						activeTab={activeTab === TABS.MARKETER}
						clickHandler={() => {
							history.push({ search: '?marketers' });
						}}
					/>
				</TabNav>
				<UsersTable setPagination={handleSetPagination} isLoading={isLoading} />
			</div>
		</div>
	);
};

export default UserListContent;
